import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import axios from 'axios';
import { ArrowLeft, User, Mail, Building, Phone, Calendar, FileText, FileSignature, Bell, Send, CheckCircle, AlertCircle, Edit, Upload, Trash2, Download, X } from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const ClientDetail = () => {
  const { email } = useParams();
  const navigate = useNavigate();
  const { token } = useAdminAuth();
  const fileInputRef = useRef(null);
  
  const [client, setClient] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [emailMessage, setEmailMessage] = useState({ type: '', text: '' });
  
  // Upload states
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState({ type: '', text: '' });
  const [description, setDescription] = useState('');
  const [showUploadForm, setShowUploadForm] = useState(false);

  useEffect(() => {
    fetchClientData();
  }, [email]);

  const fetchClientData = async () => {
    try {
      const [clientRes, docsRes, contractsRes] = await Promise.all([
        axios.get(`${API}/admin/clients/${encodeURIComponent(email)}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/documents/${encodeURIComponent(email)}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/contracts/client/${encodeURIComponent(email)}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setClient(clientRes.data);
      setDocuments(docsRes.data);
      setContracts(contractsRes.data);
    } catch (error) {
      console.error('Error fetching client data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setResendingEmail(true);
    setEmailMessage({ type: '', text: '' });
    
    try {
      await axios.post(
        `${API}/admin/clients/${encodeURIComponent(email)}/resend-invite`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEmailMessage({ type: 'success', text: 'Email de primeiro acesso reenviado com sucesso!' });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao reenviar email';
      setEmailMessage({ type: 'error', text: errorMsg });
    } finally {
      setResendingEmail(false);
    }
  };

  // Document download handler
  const handleDocumentDownload = async (doc) => {
    try {
      const baseUrl = window.location.origin;
      const response = await axios.get(
        `${baseUrl}${doc.file_url}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename || 'documento.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Erro ao baixar documento');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadMessage({ type: '', text: '' });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', description);

    try {
      await axios.post(
        `${API}/admin/documents/upload/${encodeURIComponent(email)}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setUploadMessage({ type: 'success', text: 'Documento enviado com sucesso!' });
      setDescription('');
      setShowUploadForm(false);
      fetchClientData(); // Refresh documents list
      
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao enviar documento';
      setUploadMessage({ type: 'error', text: errorMsg });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId, filename) => {
    if (!window.confirm(`Excluir o documento "${filename}"?`)) return;

    try {
      await axios.delete(`${API}/admin/documents/admin/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchClientData(); // Refresh documents list
    } catch (error) {
      alert('Erro ao excluir documento');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Check if client needs first access email (no password set)
  const needsFirstAccess = client && (!client.password_set && (!client.hashed_password || client.hashed_password === ''));

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-arcano-blue mx-auto"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <User size={48} className="mx-auto mb-4 text-gray-400" />
        <p className="text-gray-500">Cliente não encontrado</p>
        <button
          onClick={() => navigate('/admin/clients')}
          className="mt-4 text-arcano-blue hover:underline"
        >
          Voltar para lista
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/clients')}
          className="p-2 hover:bg-gray-100 rounded-md transition-colors"
        >
          <ArrowLeft size={24} className="text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900">{client.full_name}</h1>
          <p className="text-gray-600">Detalhes do cliente</p>
        </div>
        <Link
          to={`/admin/clients/${encodeURIComponent(email)}/edit`}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
        >
          <Edit size={18} />
          Editar
        </Link>
      </div>

      {/* Email Message */}
      {emailMessage.text && (
        <div className={`flex items-center gap-2 p-4 rounded-lg ${
          emailMessage.type === 'success' 
            ? 'bg-green-50 text-green-700 border border-green-200' 
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {emailMessage.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          {emailMessage.text}
        </div>
      )}

      {/* First Access Warning */}
      {needsFirstAccess && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-yellow-600 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="font-medium text-yellow-800">Cliente ainda não configurou a senha</p>
              <p className="text-sm text-yellow-700 mt-1">
                O cliente precisa acessar o email de primeiro acesso para configurar sua senha e entrar no sistema.
              </p>
              <button
                onClick={handleResendEmail}
                disabled={resendingEmail}
                className="mt-3 flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors disabled:opacity-50"
              >
                {resendingEmail ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Reenviar Email de Primeiro Acesso
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Client Info */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
            <div className="flex items-center gap-3 pb-4 border-b">
              <div className="w-16 h-16 bg-arcano-blue rounded-full flex items-center justify-center">
                <User size={32} className="text-white" />
              </div>
              <div>
                <h2 className="font-bold text-lg">{client.full_name}</h2>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    client.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {client.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                  {needsFirstAccess && (
                    <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-100 text-yellow-700">
                      Aguardando senha
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3 text-gray-600">
                <Mail size={18} />
                <span>{client.email}</span>
              </div>
              {client.company && (
                <div className="flex items-center gap-3 text-gray-600">
                  <Building size={18} />
                  <span>{client.company}</span>
                </div>
              )}
              {client.phone && (
                <div className="flex items-center gap-3 text-gray-600">
                  <Phone size={18} />
                  <span>{client.phone}</span>
                </div>
              )}
              <div className="flex items-center gap-3 text-gray-600">
                <Calendar size={18} />
                <span>Cliente desde {new Date(client.created_at).toLocaleDateString('pt-BR')}</span>
              </div>
            </div>

            <div className="pt-4 border-t space-y-2">
              <Link
                to={`/admin/contracts/new?client=${encodeURIComponent(client.email)}`}
                className="w-full flex items-center justify-center gap-2 bg-arcano-blue text-white px-4 py-2 rounded-md hover:bg-arcano-blue-dark transition-colors"
              >
                <FileSignature size={18} />
                Novo Contrato
              </Link>
              <Link
                to={`/admin/notifications/new?client=${encodeURIComponent(client.email)}`}
                className="w-full flex items-center justify-center gap-2 border border-arcano-blue text-arcano-blue px-4 py-2 rounded-md hover:bg-blue-50 transition-colors"
              >
                <Bell size={18} />
                Enviar Notificação
              </Link>
            </div>
          </div>
        </div>

        {/* Documents & Contracts */}
        <div className="lg:col-span-2 space-y-6">
          {/* Documents */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="text-arcano-blue" />
                Documentos ({documents.length})
              </h3>
              <button
                onClick={() => setShowUploadForm(!showUploadForm)}
                className="flex items-center gap-2 px-4 py-2 bg-arcano-blue text-white rounded-md hover:bg-arcano-blue-dark transition-colors"
              >
                <Upload size={18} />
                Anexar Documento
              </button>
            </div>

            {/* Upload Form */}
            {showUploadForm && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-700">Enviar novo documento</h4>
                  <button
                    onClick={() => setShowUploadForm(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={20} />
                  </button>
                </div>
                
                {uploadMessage.text && (
                  <div className={`mb-3 flex items-center gap-2 p-3 rounded-md ${
                    uploadMessage.type === 'success' 
                      ? 'bg-green-50 text-green-700' 
                      : 'bg-red-50 text-red-700'
                  }`}>
                    {uploadMessage.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                    {uploadMessage.text}
                  </div>
                )}

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Descrição (opcional)
                    </label>
                    <input
                      type="text"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Ex: Contrato assinado, Nota fiscal..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Arquivo
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-arcano-blue file:text-white hover:file:bg-arcano-blue-dark"
                    />
                  </div>
                  {uploading && (
                    <div className="flex items-center gap-2 text-arcano-blue">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-arcano-blue"></div>
                      Enviando...
                    </div>
                  )}
                </div>
              </div>
            )}

            {documents.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Nenhum documento enviado</p>
            ) : (
              <div className="space-y-2">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                    <div className="flex items-center gap-3">
                      <FileText size={20} className="text-gray-500" />
                      <div>
                        <p className="font-medium">{doc.filename}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <span>{new Date(doc.uploaded_at).toLocaleDateString('pt-BR')}</span>
                          {doc.file_size && <span>• {formatFileSize(doc.file_size)}</span>}
                          {doc.description && <span>• {doc.description}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleDocumentDownload(doc)}
                        className="p-2 text-arcano-blue hover:bg-blue-50 rounded-md transition-colors"
                        title="Baixar"
                      >
                        <Download size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteDocument(doc.id, doc.filename)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                        title="Excluir"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Contracts */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2 mb-4">
              <FileSignature className="text-arcano-blue" />
              Contratos ({contracts.length})
            </h3>
            {contracts.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Nenhum contrato registrado</p>
            ) : (
              <div className="space-y-2">
                {contracts.map((contract) => (
                  <Link
                    key={contract.id}
                    to={`/admin/contracts/${contract.id}`}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
                  >
                    <div>
                      <p className="font-medium">R$ {contract.total_value?.toLocaleString('pt-BR')}</p>
                      <p className="text-sm text-gray-500">
                        {contract.installments}x de R$ {contract.installment_value?.toLocaleString('pt-BR')}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      contract.status === 'active' ? 'bg-green-100 text-green-700' :
                      contract.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      contract.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {contract.status === 'active' ? 'Ativo' :
                       contract.status === 'pending' ? 'Pendente' :
                       contract.status === 'completed' ? 'Concluído' :
                       contract.status === 'cancelled' ? 'Cancelado' : contract.status}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDetail;
